/**
 * Invented radar data for the fixtures transport (dev, unit tests, E2E): an index with the last
 * 2 h of 5-minute frames and frames encoded with `encodeRadarFrame`. One rain cell drifts east
 * over western Switzerland; the far east (Engadin) is nodata (a radar coverage hole).
 * Nothing here is real MeteoSwiss data.
 */
import {
  RADAR_FRAME,
  RADAR_MAX_UNITS,
  RADAR_NODATA,
  RADAR_STEP_MS,
  RADAR_WINDOW_MS,
  encodeRadarFrame,
  parseRadarStamp,
  radarCellCenter,
  radarUnits,
} from "@divespots/contracts";
import type { RadarIndex } from "@divespots/contracts";

/**
 * What the fixtures radar does: normal, index answers 503, one frame answers 404, forecast routes
 * down, or "off" (the radar routes answer 404 like an API without the radar: the plain forecast bar).
 */
export type RadarFixtureMode = "ok" | "stale" | "bad-frame" | "forecast-down" | "off";

const MODES: readonly RadarFixtureMode[] = ["ok", "stale", "bad-frame", "forecast-down", "off"];
let override: RadarFixtureMode | null = null;

/** Unit tests set the mode directly; null goes back to the E2E storage key. */
export function setRadarFixtureMode(m: RadarFixtureMode | null): void {
  override = m;
}

/** E2E builds only: `localStorage["divespots.e2e.radar"]` picks the mode. */
export function radarFixtureMode(): RadarFixtureMode {
  if (override) return override;
  if (import.meta.env?.VITE_E2E !== "1" || typeof localStorage === "undefined") return "ok";
  try {
    const v = localStorage.getItem("divespots.e2e.radar");
    return MODES.find((m) => m === v) ?? "ok";
  } catch {
    return "ok";
  }
}

/** Newest frame: about 4 minutes behind the clock, like the real product. */
export const newestFrameMs = (nowMs: number): number =>
  Math.floor((nowMs - 4 * 60_000) / RADAR_STEP_MS) * RADAR_STEP_MS;

export function makeRadarIndexFixture(nowMs: number): RadarIndex {
  const newest = newestFrameMs(nowMs);
  const n = RADAR_WINDOW_MS / RADAR_STEP_MS;
  const frames: string[] = [];
  for (let k = n; k >= 0; k--) frames.push(new Date(newest - k * RADAR_STEP_MS).toISOString());
  return {
    source: "meteoswiss-rzc",
    frames,
    fetchedAt: new Date(nowMs).toISOString(),
    attribution: "MeteoSwiss",
  };
}

/** The frame that answers 404 in the "bad-frame" mode: 10 minutes before the newest. */
export const badFrameMs = (nowMs: number): number => newestFrameMs(nowMs) - 2 * RADAR_STEP_MS;

/** Fixed when the module loads, so a frame time always draws the same picture. */
const ANCHOR_MS = newestFrameMs(Date.now());
/** The cell moves 0.012° east per frame: about 10 km/h. */
const CELL = { lon: 7.0, lat: 46.55, dLonPerFrame: 0.012, rLon: 0.3, rLat: 0.2, peakMmH: 30 };

const lons = Array.from({ length: RADAR_FRAME.width }, (_, i) => radarCellCenter(i, 0).lon);
const lats = Array.from({ length: RADAR_FRAME.height }, (_, j) => radarCellCenter(0, j).lat);

const cache = new Map<number, ArrayBuffer>();

/** The encoded frame for a time on the 5-minute grid. */
export function makeRadarFrameFixture(timeMs: number): ArrayBuffer {
  const hit = cache.get(timeMs);
  if (hit) return hit;
  const { width, height } = RADAR_FRAME;
  const cells = new Uint8Array(width * height);
  const cx = CELL.lon + ((timeMs - ANCHOR_MS) / RADAR_STEP_MS) * CELL.dLonPerFrame;
  for (let j = 0; j < height; j++) {
    const lat = lats[j]!;
    for (let i = 0; i < width; i++) {
      const lon = lons[i]!;
      let v: number;
      if (lon > 10.0) v = RADAR_NODATA;
      else {
        const dx = (lon - cx) / CELL.rLon;
        const dy = (lat - CELL.lat) / CELL.rLat;
        v = radarUnits(CELL.peakMmH * Math.exp(-2.2 * (dx * dx + dy * dy)));
      }
      cells[j * width + i] = Math.min(v, v === RADAR_NODATA ? v : RADAR_MAX_UNITS);
    }
  }
  const buf = encodeRadarFrame({ timeMs, cells });
  if (cache.size > 40) cache.clear();
  cache.set(timeMs, buf);
  return buf;
}

/** Frame time for a request's `stamp`, or null for anything that is not a frame we would hold. */
export function frameTimeForStamp(stamp: string, nowMs: number): number | null {
  const ms = parseRadarStamp(stamp);
  if (ms === null) return null;
  const newest = newestFrameMs(nowMs);
  return ms <= newest && ms >= newest - RADAR_WINDOW_MS - RADAR_STEP_MS ? ms : null;
}
